import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLocale, LANGUAGES, CURRENCIES } from "@/context/locale-context";
import { useCart } from "@/context/cart-context";
import { CartDrawer } from "@/components/cart-drawer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { CookieConsent, openCookieSettings } from "@/components/CookieConsent";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingBag, Menu, X } from "lucide-react";

const NAV_LINKS = [
  { to: "/gallery", labelKey: "gallery",   label: "Galería"   },
  { to: "/artist",  labelKey: "biography", label: "Biografía" },
  { to: "/contact", labelKey: "contact",   label: "Contacto"  },
];

export function Layout({ children }) {
  const location = useLocation();
  const { t, language, currency, setLanguage, setCurrency } = useLocale();
  const { count, openCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-[100dvh] flex flex-col">

      {/* ══════════════════════════════════════════════
          HEADER
      ══════════════════════════════════════════════ */}
      <header
        className="sticky top-0 z-50 w-full border-b bg-background/90 backdrop-blur-sm"
        data-testid="main-header"
      >
        <div className="container mx-auto px-4 md:px-8 h-14 md:h-16 flex items-center justify-between gap-4">

          {/* ── Logo: NUNCA desaparece ── */}
          <Link
            to="/"
            className="font-serif text-lg md:text-2xl tracking-tight shrink-0 hover:opacity-80 transition-opacity"
            data-testid="logo-link"
          >
            Taller del Profe
          </Link>

          {/* ── Nav desktop ── */}
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest text-muted-foreground">
            {NAV_LINKS.map(({ to, labelKey, label }) => (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${labelKey}`}
                className={`hover:text-foreground transition-colors ${
                  isActive(to) ? "text-foreground font-medium" : ""
                }`}
              >
                {t.nav?.[labelKey] || label}
              </Link>
            ))}
          </nav>

          {/* ── Controles derecha ── */}
          <div className="flex items-center gap-1 md:gap-2 shrink-0">

            {/* Selectores: ocultos en móvil para no saturar */}
            <div className="hidden sm:flex items-center gap-1.5">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger
                  className="h-8 w-[70px] md:w-[110px] text-[10px] md:text-xs rounded-none border-muted bg-transparent focus:ring-0 focus:ring-offset-0"
                  data-testid="language-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code} className="text-xs">
                      <span className="mr-1.5">{lang.flag}</span>
                      <span className="hidden md:inline">{lang.label}</span>
                      <span className="md:hidden">{lang.code.toUpperCase()}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger
                  className="h-8 w-[70px] md:w-[110px] text-[10px] md:text-xs rounded-none border-muted bg-transparent focus:ring-0 focus:ring-offset-0"
                  data-testid="currency-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(cur => (
                    <SelectItem key={cur.code} value={cur.code} className="text-xs">
                      <span className="font-mono mr-1">{cur.symbol}</span>{cur.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Carrito: NUNCA desaparece */}
            <button
              onClick={openCart}
              className="relative flex items-center justify-center w-9 h-9 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t.cart?.title || "Carrito"}
              data-testid="cart-button"
            >
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-foreground text-background text-[10px] font-medium rounded-full flex items-center justify-center leading-none">
                  {count}
                </span>
              )}
            </button>

            {/* Hamburguesa: solo móvil */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden flex items-center justify-center w-9 h-9 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* ── Menú móvil desplegable ── */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileOpen ? "max-h-screen border-t" : "max-h-0"
          } bg-background`}
        >
          {/* Nav links */}
          <nav className="container mx-auto px-4 pt-4 pb-2 flex flex-col">
            {NAV_LINKS.map(({ to, labelKey, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`py-3 text-sm uppercase tracking-widest border-b border-border/40 transition-colors ${
                  isActive(to)
                    ? "text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.nav?.[labelKey] || label}
              </Link>
            ))}
          </nav>

          {/* Selectores dentro del menú móvil */}
          <div className="container mx-auto px-4 py-4 flex items-center gap-2 sm:hidden">
            <Select value={language} onValueChange={(v) => { setLanguage(v); }}>
              <SelectTrigger className="h-8 flex-1 text-[10px] rounded-none border-muted bg-transparent focus:ring-0 focus:ring-offset-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map(lang => (
                  <SelectItem key={lang.code} value={lang.code} className="text-xs">
                    <span className="mr-1.5">{lang.flag}</span>{lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={currency} onValueChange={(v) => { setCurrency(v); }}>
              <SelectTrigger className="h-8 flex-1 text-[10px] rounded-none border-muted bg-transparent focus:ring-0 focus:ring-offset-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(cur => (
                  <SelectItem key={cur.code} value={cur.code} className="text-xs">
                    <span className="font-mono mr-1">{cur.symbol}</span>{cur.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <CartDrawer />
      <WhatsAppButton />
      <main className="flex-1">{children}</main>
      <CookieConsent />

      {/* ══════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════ */}
      <footer className="border-t bg-card py-12 mt-24" data-testid="main-footer">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            {/* Brand */}
            <div>
              <h3 className="font-serif text-xl mb-4">Taller del Profe.</h3>
              <p className="text-muted-foreground text-sm max-w-sm">{t.footer?.tagline}</p>
              <p className="text-muted-foreground text-sm max-w-sm"></p>
              
              <h4 className="font-serif text-xl mb-2">
                Contacto:
                <p className="text-muted-foreground text-sm max-w-sm">{t.footer?.contacto}</p>

              </h4>
            </div>

            {/* Explorar */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
                {t.nav?.explore || "Explorar"}
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/gallery" className="hover:text-foreground transition-colors">
                    {t.nav?.allWorks || "Todas las obras"}
                  </Link>
                </li>
                <li>
                  <Link to="/gallery?featured=true" className="hover:text-foreground transition-colors">
                    {t.nav?.featured || "Destacadas"}
                  </Link>
                </li>
                <li>
                  <Link to="/gallery?onSale=true" className="hover:text-foreground transition-colors">
                    {t.nav?.onSale || "En venta"}
                  </Link>
                </li>
                <li>
                  <Link to="/artist" className="hover:text-foreground transition-colors">
                    {t.nav?.biography || "Biografía"}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-foreground transition-colors">
                    {t.nav?.contact || "Contacto"}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Carrito */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
                {t.cart?.title || "Carrito"}
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <button
                    className="hover:text-foreground transition-colors text-left"
                    onClick={openCart}
                  >
                    {t.cart?.addToCart || "Ver carrito"}
                  </button>
                </li>
                <li>
                  <Link to="/checkout" className="hover:text-foreground transition-colors">
                    {t.cart?.checkout || "Checkout"}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t text-sm text-muted-foreground flex justify-between items-center">
            <p>&copy; {new Date().getFullYear()} {t.footer?.copyright}</p>
          </div>
          <nav aria-label="Información legal" className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <Link to="/legal/privacy" className="hover:text-foreground underline-offset-4 hover:underline">Tratamiento de datos</Link>
            <Link to="/legal/terms" className="hover:text-foreground underline-offset-4 hover:underline">Términos y condiciones</Link>
            <Link to="/legal/cookies" className="hover:text-foreground underline-offset-4 hover:underline">Política de cookies</Link>
            <Link to="/legal/shipping-returns" className="hover:text-foreground underline-offset-4 hover:underline">Envíos, retractos y garantías</Link>
            <button type="button" onClick={openCookieSettings} className="text-left hover:text-foreground underline-offset-4 hover:underline">
              Preferencias de cookies
            </button>
          </nav>
        </div>
      </footer>
    </div>
  );
}